import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { RHFTextField } from 'src/components/hook-form';
import { UploadErrorMessage } from 'src/components/hook-form/UploadErrorMessage';
import RHFUploadImage from 'src/components/hook-form/uploadImage/RHFUploadImage';
import Iconify from 'src/components/iconify';
import useResponsive from 'src/hooks/useResponsive';
import {
  DeleteButton,
  FormInnerContainer,
  FormItem,
  FormLabel,
  FormRow,
  ImageContainer,
} from 'src/layouts/form/FormLayout';
import { getFileUrl } from 'src/utils/fileUploadUtils';

import { useCompanyInfoByLanguage } from './hooks/useCompanyInfoByLangCd';

interface IBasicInfoProps {
  langCd: string;
}

export const BasicInfo = ({ langCd }: IBasicInfoProps) => {
  const { t } = useTranslation();
  const { formState, setValue, watch } = useFormContext();
  const isDesktopXl = useResponsive('up', 'xl');
  // 언어에 해당하는 회사 정보, index 반환
  const { index } = useCompanyInfoByLanguage(langCd);

  // 현재 파일과 경로 값을 실시간으로 감시
  const currentFile = watch(`companyInfos.${index}.logoImgFile`);
  const currentPath = watch(`companyInfos.${index}.logoImgPath`);

  return (
    <FormInnerContainer>
      <FormLabel>{t('common.form.section.basicInfo')}</FormLabel>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isDesktopXl ? 'row' : 'column',
          gap: 4,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* 로고 */}
        <FormInnerContainer>
          <FormLabel>{t('settings.form.basicInfo.logo')}</FormLabel>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
              {/* 이미지가 있을 경우 보여주기 */}
              {currentFile || currentPath ? (
                <ImageContainer sx={{ width: 150, height: 150 }}>
                  <img
                    src={currentFile ? URL.createObjectURL(currentFile) : getFileUrl(currentPath)}
                    alt="logo"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <DeleteButton
                    onClick={() => {
                      setValue(`companyInfos.${index}.logoImgFile`, null);
                      setValue(`companyInfos.${index}.logoImgPath`, null);
                    }}
                  >
                    <Iconify icon="eva:close-fill" width={20} />
                  </DeleteButton>
                </ImageContainer>
              ) : (
                /* 이미지 업로드 컴포넌트 */
                <RHFUploadImage
                  name={`companyInfos.${index}.logoImgFile`}
                  files={[]}
                  onChange={(files) => {
                    if (files[0]) {
                      setValue(`companyInfos.${index}.logoImgFile`, files[0]);
                      setValue(`companyInfos.${index}.logoImgPath`, null);
                    }
                  }}
                  accept={{ 'image/*': [] }}
                  maxFiles={1}
                />
              )}

              {formState.errors?.logoImgFile?.message && (
                <UploadErrorMessage message={String(formState.errors?.logoImgFile?.message)} />
              )}
            </Box>
          </FormRow>
        </FormInnerContainer>
        <Box sx={{ width: '100%' }}>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                name={`companyInfos.${index}.companyNm`}
                label={t('settings.form.basicInfo.name')}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                name={`companyInfos.${index}.address`}
                label={t(`settings.form.basicInfo.address${langCd === 'en' ? '(en)' : '(ko)'}`)}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                name={`companyInfos.${index}.tel`}
                label={t('settings.form.basicInfo.tel')}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                name={`companyInfos.${index}.fax`}
                label={t('settings.form.basicInfo.fax')}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                name={`companyInfos.${index}.copyright`}
                label={t('settings.form.basicInfo.copyright')}
              />
            </FormItem>
          </FormRow>
        </Box>
      </Box>
    </FormInnerContainer>
  );
};

export default observer(BasicInfo);
